import type { ComponentProps } from 'react';

import { clsxm } from '@vs-shop/utils';

/**
 * A customer review as a card: the quote, who left it, when, and their score.
 * Flat named parts a tenant arranges in its own JSX, each painting itself.
 *
 * The parts are flat because the two brands that render this section order them
 * differently and neither order may be baked in:
 *
 *   rcprofi      text → (mark, name, date, stars)
 *   RacingLine   mark → stars → (name, date) → text
 *
 * Same parts, two arrangements, no prop between them. Only the pairing of name
 * and date is shared, so that is the only grouping the section owns; every
 * other grouping is layout, and layout is tenant JSX.
 *
 *   <ReviewCard className="justify-between">
 *     <ReviewCardText>{text}</ReviewCardText>
 *
 *     <div className="flex gap-x-5">
 *       <ReviewCardIcon>
 *         <SourceMark />
 *       </ReviewCardIcon>
 *       <div className="flex flex-col gap-y-2">
 *         <ReviewCardMeta>
 *           <ReviewCardAuthor>{name}</ReviewCardAuthor>
 *           <ReviewCardDate>{posted}</ReviewCardDate>
 *         </ReviewCardMeta>
 *         <RatingStars rating={rating} />
 *       </div>
 *     </div>
 *   </ReviewCard>
 *
 * The stars are `@vs-shop/ui`'s `RatingStars` composed directly: a score readout
 * carries no commerce meaning, so it stays a primitive rather than becoming a
 * part of this section.
 *
 * The card paints for an inverted surface, which is where both brands place it.
 * The colours are semantic tokens, so one file reads in each brand's own
 * palette; a placement on a light surface is a `className` away.
 *
 * No `variant` anywhere: the card paints one way and the arrangement is what
 * changes between tenants.
 *
 * Server-first — every part is a plain server component.
 */

/**
 * The card's box, filling the cell a carousel or grid gives it.
 *
 * It distributes NOTHING: how the composed parts spread over that height is the
 * arrangement, and the arrangement differs per tenant. A `justify-between` here
 * would read as "quote at the top, attribution at the bottom" — true of one
 * brand's order and meaningless in the other's.
 */
const reviewCardClasses = 'flex h-full flex-col gap-y-6';

export type ReviewCardProps = ComponentProps<'div'>;

/** Root of the section. */
export const ReviewCard = ({ className, ...props }: ReviewCardProps) => (
  <div data-slot="review-card" className={clsxm(reviewCardClasses, className)} {...props} />
);

/**
 * The quote. Clamped at three lines so a row of cards keeps its height whatever
 * a reviewer wrote.
 */
const reviewCardTextClasses = 'text-foreground-inverted/80 line-clamp-3 text-base/6';

export type ReviewCardTextProps = ComponentProps<'p'>;

/** The quote. */
export const ReviewCardText = ({ className, ...props }: ReviewCardTextProps) => (
  <p data-slot="review-card-text" className={clsxm(reviewCardTextClasses, className)} {...props} />
);

/**
 * The box the review source's mark sits in — a marketplace logo, a brand
 * device. Sized by the card rather than by the mark, for the same reason the
 * strip's icon frame is: the asset is the tenant's, the size is the section's.
 *
 * `shrink-0` is load-bearing. Without it the quote beside a wide mark squeezes
 * it narrower than its own box, which is a defect no class list can show: the
 * mark keeps every class it asked for and simply renders smaller.
 */
const reviewCardIconClasses = 'block size-12 shrink-0 [&>svg]:size-full';

export type ReviewCardIconProps = ComponentProps<'span'>;

/** The frame the review source's mark fills. */
export const ReviewCardIcon = ({ className, ...props }: ReviewCardIconProps) => (
  <span
    data-slot="review-card-icon"
    className={clsxm(reviewCardIconClasses, className)}
    {...props}
  />
);

/**
 * Who left the review and when, side by side. The one grouping both brands
 * share, which is why it is a part and the boxes around it are not.
 */
const reviewCardMetaClasses = 'flex gap-x-2';

export type ReviewCardMetaProps = ComponentProps<'div'>;

/** The reviewer's name beside the date. */
export const ReviewCardMeta = ({ className, ...props }: ReviewCardMetaProps) => (
  <div
    data-slot="review-card-meta"
    className={clsxm(reviewCardMetaClasses, className)}
    {...props}
  />
);

/** The reviewer's name — full strength against the muted date beside it. */
const reviewCardAuthorClasses = 'text-foreground-inverted text-base/4';

export type ReviewCardAuthorProps = ComponentProps<'span'>;

/**
 * The reviewer's name. Whether an anonymous review falls back to a label or
 * renders nothing is the tenant's copy decision, so this part carries no
 * fallback of its own.
 */
export const ReviewCardAuthor = ({ className, ...props }: ReviewCardAuthorProps) => (
  <span
    data-slot="review-card-author"
    className={clsxm(reviewCardAuthorClasses, className)}
    {...props}
  />
);

/** When the review was left — smaller and muted beside the name. */
const reviewCardDateClasses = 'text-foreground-inverted/80 text-sm/4';

export type ReviewCardDateProps = ComponentProps<'span'>;

/**
 * The review's date. Takes formatted copy as children: how a date reads is
 * locale wiring, which stays in the tenant's JSX.
 */
export const ReviewCardDate = ({ className, ...props }: ReviewCardDateProps) => (
  <span
    data-slot="review-card-date"
    className={clsxm(reviewCardDateClasses, className)}
    {...props}
  />
);
